import { CTA_TYPES, PAGE_LEVEL_VIDEO_CTA } from '../core/constants';

const { TEXT_MAX_LENGTH, HTTP_URL_PREFIX, URL_WHITESPACE } = PAGE_LEVEL_VIDEO_CTA;

/**
 * Reads a video's CTA override, returning an empty object when none is configured.
 * @param {Object} video - Playlist video.
 * @returns {Object} Nested CTA text and URL fields.
 */
export const getVideoCtaDetails = (video) => video.videoLvlCtaDetails || {};

/**
 * Validates supplied CTA fields while allowing empty or incomplete overrides.
 * @param {Object} [details={}] - CTA fields to validate.
 * @param {string} [details.ctaText=''] - Button label.
 * @param {string} [details.ctaUrl=''] - Absolute HTTP or HTTPS destination.
 * @returns {Object<string, string>} Error messages keyed by invalid field name.
 */
export const getCtaErrors = ({ ctaText = '', ctaUrl = '' } = {}) => {
	const errors = {};
	if (ctaText.trim().length > TEXT_MAX_LENGTH) {
		errors.ctaText = `Use ${TEXT_MAX_LENGTH} characters or fewer.`;
	}
	const trimmedUrl = ctaUrl.trim();
	if (trimmedUrl) {
		try {
			const url = new URL(trimmedUrl);
			if (!HTTP_URL_PREFIX.test(trimmedUrl) || URL_WHITESPACE.test(trimmedUrl) || !url.hostname) {
				throw new Error('Invalid CTA URL');
			}
		} catch {
			errors.ctaUrl = 'Enter a full URL starting with http:// or https://.';
		}
	}
	return errors;
};

/**
 * Checks that both CTA fields contain non-whitespace text.
 * @param {Object} [details] - Optional nested CTA fields.
 * @returns {boolean} Whether both text and URL are present.
 */
const hasCompleteCta = (details) => !!(details?.ctaText?.trim() && details?.ctaUrl?.trim());

/**
 * Describes the selected CTA level and whether a video override is valid and complete.
 * @param {Object} video - Playlist video containing its CTA type and optional details.
 * @returns {string} Status text for the editor.
 */
export const getVideoCtaStatus = (video) => {
	if (video.ctaType !== CTA_TYPES.VIDEO_LVL) {
		return 'Using the section CTA.';
	}
	if (Object.keys(getCtaErrors(getVideoCtaDetails(video))).length) {
		return 'Fix the CTA fields before saving.';
	}
	if (hasCompleteCta(video.videoLvlCtaDetails)) {
		return 'Using this video’s CTA.';
	}
	return 'No video CTA override. The player resolves its defaults during playback.';
};

/**
 * Updates one video immutably, preserving other pages and videos by reference.
 * @param {Object[]} data - Current page configurations.
 * @param {string} pageId - ID of the page containing the video.
 * @param {number} videoIndex - Zero-based index of the video to update.
 * @param {function(Object): Object} updateVideo - Returns the replacement video.
 * @returns {Object[]} Page configurations containing the updated video.
 */
export const updatePlaylistVideo = (data, pageId, videoIndex, updateVideo) =>
	data.map((page) =>
		page.id === pageId
			? {
					...page,
					videos: page.videos.map((video, index) =>
						index === videoIndex ? updateVideo(video) : video
					)
				}
			: page
	);

/**
 * Checks all playlist CTA fields before saving; absent or incomplete overrides are allowed.
 * @param {Object[]} data - Page configurations containing video arrays.
 * @returns {void}
 * @throws {Error} When a field is invalid, including its page path and video number.
 */
export const validatePlaylistCtas = (data) => {
	data.forEach((page) => {
		page.videos.forEach((video, index) => {
			const errors = getCtaErrors(getVideoCtaDetails(video));
			const error = errors.ctaText || errors.ctaUrl;
			if (error) throw new Error(`${page.pageUrl}, video ${index + 1}: ${error}`);
		});
	});
};
